import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Lock, Clock, Loader2, Search, AlertTriangle, Sparkles, ChevronDown, ArrowLeft, Trophy, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useApp } from '../App';
import { getChests, getRandomAvailableChest, searchChest, getRaffleDetail } from '../lib/api';
import ChestModal from '../components/ChestModal';
import { toast } from 'sonner';

const CHESTS_PER_PAGE = 100;

const ChestCell = React.memo(({ chest, onClick, highlighted }) => {
  const isAvailable = chest.state === 'available';
  const isReserved = chest.state === 'reserved';
  const isSold = chest.state === 'sold';

  return (
    <button 
      onClick={() => isAvailable && onClick(chest.chest_number)} 
      disabled={isSold} 
      id={`chest-${chest.chest_number}`}
      className={`relative w-full aspect-square flex items-center justify-center transition-all duration-[250ms] ease-out group
        ${isAvailable ? 'cursor-pointer hover:scale-105' : ''}
        ${isReserved ? 'cursor-not-allowed' : ''}
        ${isSold ? 'cursor-not-allowed' : ''}
        ${highlighted ? 'scale-105 z-10' : ''}`}
      data-testid={`chest-cell-${chest.chest_number}`}>
      {/* Cofre con número overlay */}
      <div className="relative w-[85%] h-[85%] flex items-center justify-center">
        <img 
          src={isSold ? "/images/cofre_abierto.png" : "/images/cofre_cerrado.png"} 
          alt="Cofre" 
          className={`w-full h-full object-contain transition-all duration-[250ms] ease-out
            ${isAvailable ? 'drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)] group-hover:drop-shadow-[0_0_20px_rgba(212,175,55,0.5)]' : ''}
            ${isReserved ? 'opacity-60 drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]' : ''}
            ${isSold ? 'opacity-50 drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]' : ''}
            ${highlighted ? 'drop-shadow-[0_0_16px_rgba(255,198,64,0.6)]' : ''}`}
        />
        {/* Número sobre el cofre (overlay inferior) */}
        <span className={`absolute bottom-[8%] left-1/2 -translate-x-1/2 text-[10px] sm:text-xs font-heading font-bold drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]
          ${isAvailable ? 'text-white' : ''}
          ${isReserved ? 'text-[#4BA3FF]' : ''}
          ${isSold ? 'text-[#FFC640]' : ''}`}>
          {chest.chest_label}
        </span>
      </div>
      {/* Estado debajo del cofre */}
      <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 text-[8px] sm:text-[9px] font-medium
        ${isAvailable ? 'text-[#FFC640]' : ''} 
        ${isReserved ? 'text-[#FF9F43]' : ''} 
        ${isSold ? 'text-[#7A7F8A]' : ''}`}>
        {isAvailable ? 'Disponible' : isReserved ? 'Reservado' : 'Vendido'}
      </span>
    </button>
  );
});

const Cofres = () => {
  const { user, setSelectedRaffleId, fetchCart } = useApp();
  const navigate = useNavigate();
  const { raffleId } = useParams();
  const [raffle, setRaffle] = useState(null);
  const [chests, setChests] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedChest, setSelectedChest] = useState(null);
  const [highlightedChest, setHighlightedChest] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [luckyLoading, setLuckyLoading] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => { if (!user) navigate('/registro'); }, [user, navigate]);

  // Set global selected raffle + load raffle detail
  useEffect(() => {
    if (raffleId) {
      setSelectedRaffleId(raffleId);
      getRaffleDetail(raffleId).then(res => setRaffle(res.data)).catch(() => navigate('/cofres'));
    }
  }, [raffleId, setSelectedRaffleId, navigate]);

  // Fetch cart for this raffle
  useEffect(() => { if (user && raffleId) fetchCart(); }, [user, raffleId, fetchCart]);

  const fetchChests = useCallback(async (p) => {
    if (!user || !raffleId) return;
    setLoading(true);
    try {
      const res = await getChests(raffleId, p, CHESTS_PER_PAGE);
      setChests(res.data.chests); setTotalPages(res.data.total_pages);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [user, raffleId]);

  useEffect(() => { if (user && raffleId) fetchChests(page); }, [page, fetchChests, user, raffleId]);

  const goToChest = useCallback((chestNumber) => {
    const targetPage = Math.floor(chestNumber / CHESTS_PER_PAGE) + 1;
    setHighlightedChest(chestNumber);
    if (targetPage !== page) setPage(targetPage);
    setTimeout(() => { const el = document.getElementById(`chest-${chestNumber}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 500);
    setTimeout(() => setHighlightedChest(null), 4000);
  }, [page]);

  const handleSearch = async () => {
    const num = parseInt(searchValue.replace('#', ''), 10);
    if (isNaN(num) || num < 0 || num > 4999) { toast.error('Cofre no válido (0000-4999)'); return; }
    try {
      const res = await searchChest(num, raffleId);
      const label = res.data.state === 'available' ? 'Disponible' : res.data.state === 'reserved' ? 'Reservado' : 'Vendido';
      toast.info(`Cofre #${String(num).padStart(4,'0')}: ${label}`);
      goToChest(num);
    } catch (err) { toast.error(err.response?.data?.detail || 'No encontrado'); }
  };

  const handleLucky = async () => {
    setLuckyLoading(true);
    try {
      const anim = setInterval(() => setSearchValue(String(Math.floor(Math.random()*5000)).padStart(4,'0')), 80);
      const res = await getRandomAvailableChest(raffleId);
      setTimeout(() => { clearInterval(anim); setSearchValue(String(res.data.chest_number).padStart(4,'0')); toast.success(`¡La suerte eligió el cofre ${res.data.chest_label}!`); goToChest(res.data.chest_number); setLuckyLoading(false); }, 1200);
    } catch { setLuckyLoading(false); toast.error('No hay cofres disponibles'); }
  };

  const handlePageChange = (p) => { setPage(p); gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const handleChestStateChange = (cn, st) => setChests(prev => prev.map(c => c.chest_number === cn ? {...c, state: st} : c));

  if (!user) return null;

  const isFree = raffle?.template_type === 'free';
  const percentage = raffle?.percentage || 0;

  return (
    <div className="relative z-10">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-2">
        {/* Back + raffle info */}
        <div className="flex items-center gap-3 mb-4">
          <Link to="/cofres" className="p-2 rounded-xl bg-[#121521] border border-white/[0.08] hover:border-[rgba(255,198,64,0.3)] transition-colors">
            <ArrowLeft size={18} className="text-[#F7F2E7]/60" />
          </Link>
          <div className="flex-1">
            <h2 className="font-heading font-bold text-lg text-[#F7F2E7]">{raffle?.name || 'Cargando...'}</h2>
            <div className="flex items-center gap-3 text-xs text-[#F7F2E7]/50">
              <span className={`px-2 py-0.5 rounded-full font-heading font-bold text-[10px]
                ${isFree ? 'bg-[rgba(53,208,127,0.1)] text-[#35D07F]' : raffle?.template_type === 'mxn100' ? 'bg-[rgba(75,163,255,0.1)] text-[#4BA3FF]' : 'bg-[rgba(255,198,64,0.1)] text-[#FFC640]'}`}>
                {isFree ? 'GRATIS' : raffle?.template_type === 'mxn100' ? '$100' : '$50'}
              </span>
              <span className="flex items-center gap-1"><Trophy size={12} className="text-[#FFC640]" /> ${(isFree ? 8000 : (raffle?.prize_main_amount || 30000)).toLocaleString()}</span>
              <span>Vendidos: {raffle?.sold || 0}/{raffle?.total_chests || 5000} ({percentage}%)</span>
            </div>
          </div>
        </div>

        {/* Free notice */}
        {isFree && (
          <div className="mb-4 p-3 bg-[rgba(53,208,127,0.08)] border border-[rgba(53,208,127,0.2)] rounded-xl text-sm text-[#35D07F] flex items-center gap-2">
            <Gift size={16} /> Este es un sorteo GRATIS. Solo puedes tomar 1 cofre.
          </div>
        )}

        {/* Banner */}
        <div className="text-center mb-5">
          <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#F7F2E7] flex items-center justify-center gap-2">
            <ChevronDown size={18} className="text-[#FFC640]" /> COFRES DISPONIBLES <ChevronDown size={18} className="text-[#FFC640]" />
          </h2>
        </div>

        {/* Search + Lucky */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F7F2E7]/40" />
              <Input value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onKeyDown={(e) => e.key==='Enter' && handleSearch()}
                className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] pl-10 h-12 text-base font-heading tabular-nums focus:border-[#FFC640]"
                placeholder="Buscar cofre por número..." />
            </div>
            <Button onClick={handleSearch} className="h-12 px-5 bg-[#121521] border border-white/[0.12] text-[#F7F2E7] hover:border-[rgba(255,198,64,0.35)] rounded-xl"><Search size={18} /></Button>
          </div>
          <Button onClick={handleLucky} disabled={luckyLoading}
            className="h-12 px-6 bg-gradient-to-r from-[#D4AF37] to-[#FFC640] text-[#090A0D] font-heading font-bold text-base rounded-xl shadow-[0_10px_28px_rgba(0,0,0,0.45)] whitespace-nowrap">
            {luckyLoading ? <><Loader2 size={18} className="mr-2 animate-spin" /> Buscando...</> : <><Sparkles size={18} className="mr-2" /> Máquina de la Suerte</>}
          </Button>
        </div>

        {/* Anti-mapping */}
        <div className="flex items-start gap-3 p-3 bg-[rgba(255,180,50,0.06)] border border-[rgba(255,180,50,0.2)] rounded-xl mb-4">
          <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-300/80"><span className="font-semibold text-amber-400">IMPORTANTE:</span> Por seguridad anti-mapeo, si abres y eliminas 3 cofres sin completar la compra, tu cuenta será bloqueada por 2 horas.</p>
        </div>
      </section>

      {/* Grid */}
      <section ref={gridRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-[#F7F2E7]/50">Página {page} de {totalPages}</p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 size={32} className="animate-spin text-[#FFC640]" /></div>
        ) : (
          <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2 sm:gap-3">
            {chests.map(c => <ChestCell key={c.chest_number} chest={c} onClick={setSelectedChest} highlighted={highlightedChest===c.chest_number} />)}
          </div>
        )}
        <div className="flex items-center justify-center gap-2 mt-8">
          <Button onClick={() => handlePageChange(Math.max(1, page-1))} disabled={page===1} variant="outline" size="sm" className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] disabled:opacity-30"><ChevronLeft size={16} /></Button>
          {(() => {
            const ps = []; const s = Math.max(1, page-2); const e = Math.min(totalPages, page+2);
            if (s > 1) { ps.push(<Button key={1} onClick={() => handlePageChange(1)} variant="outline" size="sm" className="w-9 h-9 bg-[#121521] border-white/[0.12] text-[#F7F2E7] text-xs">1</Button>); if (s>2) ps.push(<span key="d1" className="text-[#F7F2E7]/30 px-1">...</span>); }
            for (let i=s; i<=e; i++) ps.push(<Button key={i} onClick={() => handlePageChange(i)} variant="outline" size="sm" className={`w-9 h-9 text-xs ${i===page?'bg-[#FFC640] text-[#090A0D] border-[#FFC640]':'bg-[#121521] border-white/[0.12] text-[#F7F2E7]'}`}>{i}</Button>);
            if (e<totalPages) { if (e<totalPages-1) ps.push(<span key="d2" className="text-[#F7F2E7]/30 px-1">...</span>); ps.push(<Button key={totalPages} onClick={() => handlePageChange(totalPages)} variant="outline" size="sm" className="w-9 h-9 bg-[#121521] border-white/[0.12] text-[#F7F2E7] text-xs">{totalPages}</Button>); }
            return ps;
          })()}
          <Button onClick={() => handlePageChange(Math.min(totalPages, page+1))} disabled={page===totalPages} variant="outline" size="sm" className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] disabled:opacity-30"><ChevronRight size={16} /></Button>
        </div>
      </section>

      <ChestModal chestNumber={selectedChest} open={selectedChest !== null} onClose={() => setSelectedChest(null)} onStateChange={handleChestStateChange} />
    </div>
  );
};

export default Cofres;
